import { FC } from 'react';
import { ActivityIndicator } from '@kontentino/ui';
import { ListPost } from 'types/PostDetail';
import UserPermissionGate from 'components/UserPermissionGate';
import ApprovalCommunicationContent from './ApprovalCommunicationContent';
import ApprovalCommunicationHeader from './ApprovalCommunicationHeader';
import ApprovalInfoAlert from './ApprovalInfoAlert';
import PostCommunicationProvider from 'app/modules/posts/modules/post/components/postCommunication/PostCommunicationProvider';
import { useUser } from 'modules/user/userSelector';
import { STATUS } from '@kontentino/kontentino-constants/Posts';
import { useApprovalCommunication } from '../../hooks/useApprovalCommunication';
import {
  getThreadApprovePermissions,
  getThreadShowPermissions,
} from '../../utils/approvalPermissions';
import { useUserPermissions } from 'app/hooks/useUserPermissions';
import { usePostDetailWorkflowHandlers } from 'app/modules/posts/modules/post/hooks/usePostDetailWorkflowHandlers';
import { usePostPopups } from 'app/modules/posts/modules/post/hooks/usePostPopups';
import useOpenPost from 'app/modules/posts/hooks/useOpenPost';

type Props = {
  post: ListPost;
  isVisible: boolean;
  onPostProcessed?: () => void;
  onApprovalPageSuccess?: () => void;
};

const ApprovalCommunicationPanel: FC<Props> = ({
  post,
  isVisible,
  onPostProcessed,
  onApprovalPageSuccess,
}) => {
  const user = useUser();
  const openPost = useOpenPost();
  const { postCommunicationRef, thread, initialActiveContent } =
    useApprovalCommunication({ post, onPostProcessed });
  const { hasSome } = useUserPermissions();
  const popups = usePostPopups();

  const workflowHandlers = usePostDetailWorkflowHandlers(
    post.id,
    popups.showPopup,
    postCommunicationRef,
    post.page,
  );

  const threadApprovePermissions = getThreadApprovePermissions(thread);
  const canApprove = hasSome(threadApprovePermissions);

  const shouldShowInfoMessage = post.status === STATUS.AT_CLIENT && !canApprove;

  const handleExpandPost = () => {
    openPost(post);
  };

  const threadPermissions = getThreadShowPermissions(thread);

  return (
    <div className="tw-flex tw-min-h-[400px] tw-w-full tw-flex-1 tw-flex-col tw-rounded-lg tw-bg-white tw-shadow-sm md:tw-min-w-[360px]">
      <ApprovalCommunicationHeader onExpand={handleExpandPost} />

      <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
        {isVisible ? (
          <UserPermissionGate exact={false} scopes={threadPermissions}>
            <PostCommunicationProvider
              ref={postCommunicationRef}
              user={user}
              postId={post.id}
              initialActiveContent={initialActiveContent}
              disableCommunicationActions={false}
              enableRespondToApprovalRequest={true}
              onApprovalPageSuccess={onApprovalPageSuccess}
            >
              <ApprovalCommunicationContent
                thread={thread}
                onApprovalPageSuccess={onApprovalPageSuccess}
              />
            </PostCommunicationProvider>
          </UserPermissionGate>
        ) : (
          <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center">
            <ActivityIndicator />
          </div>
        )}
      </div>

      {shouldShowInfoMessage && (
        <ApprovalInfoAlert
          status={post.status}
          onRespondToApprovalRequest={
            workflowHandlers.onRespondToApprovalRequest
          }
        />
      )}
    </div>
  );
};

export default ApprovalCommunicationPanel;
